// TODO
/* export enum UserType {
  ArcaneAI = 'ArcaneAI',
  User = 'User'
} */

import DOMPurify from 'dompurify';
import { marked } from 'marked';
import Mustache from 'mustache';

import { CHUNK_TYPE, TOOL_CALL_TYPE } from '~/constants/conversationModels';

import { SCOPED_MESSAGE } from '~/constants/conversationModels';
import { ensureLinksOpenInNewTab } from '~/utils/document-utils';
import { decodeHtmlEntitiesDeep } from '~/utils/html-entities';

/**
 * Renders a prompt-card template (`{{prefix:variable}}` placeholders) against filled-in
 * variables. Shared between the live submit-time flow (useConversations#processTextMessageAsPromptCardText,
 * which reads from the reactive currentPromptCard/currentFields) and the history-replay flow
 * (new-agent-messages.js#buildMessagesFromTurns, which reads from a persisted query event's
 * versionContentId/variables) so both produce byte-identical text.
 * @param {string} template - Raw prompt-card template string.
 * @param {{variable: string, value: string|string[], label?: string}[]} variables - Filled-in values.
 * @returns {string} Rendered, HTML-entity-decoded text.
 */
export const renderPromptCardText = (template, variables = []) => {
  const matches = [...(template?.matchAll(/\{\{\s*([^:]+):(\w+)\s*\}\}/g) ?? [])];

  const context = {};
  for (const [, prefix, variable] of matches) {
    const found = variables.find((v) => v.variable === variable);
    if (found) {
      context[`${prefix}:${variable}`] = () => `<span>${found.label || found.value || ''}</span>`;
    }
  }

  const output = Mustache.render(template || '', context);
  return decodeHtmlEntitiesDeep(output);
};

export const mapDtoToMessage = (receivedMessage) => {
  console.log('received Message from chatbot =>', receivedMessage);
  return {
    text: receivedMessage.data.text || 'Something went wrong. No message from chatbot was found in response',
    type: 'message',
    isUser: false,
    references: receivedMessage.data.references,
  };
};

export const encodeToBase64 = (data) => {
  // First, convert text to a byte array using TextEncoder
  const encoder = new TextEncoder();
  const utf8Bytes = encoder.encode(data);

  // Convert the byte array to a binary string
  const binaryString = new Uint8Array(utf8Bytes).reduce((acc, byte) => acc + String.fromCharCode(byte), '');

  // Now encode this binary string to base64
  const base64Encoded = btoa(binaryString);

  return base64Encoded;
};

export const reassembleMessage = (message) => {
  const chunks = message.chunks || [];
  if (chunks.length === 1) {
    return chunks[0].chunk;
  }
  const orderedChunks = chunks
    .sort((msg1, msg2) => msg1.sequenceNr - msg2.sequenceNr) // Sort the sequence numbers
    .map((msg) => msg.chunk); // Map to the actual chunks

  // Concatenate the ordered chunks
  const finalMessage = orderedChunks.join('');
  return finalMessage;
};

export const decodeFromBase64 = (data) => {
  const bytes = new Uint8Array(
    atob(data)
      .split('')
      .map((char) => char.charCodeAt(0)),
  );

  // Decode the bytes as UTF-8 text
  const decodedText = new TextDecoder('utf-8').decode(bytes);

  return decodedText;
};

// this function will be called when we retrieve a conversation by id from db
export const groupMessagesByScopedCollection = (messages) => {
  const processedMessages = [];
  messages.forEach((message, index) => {
    if (index === 0) {
      // Always add the first message
      processedMessages.push(message);
    } else {
      // Check if current message has a collectionId and it's different from the previous one
      const prevMessage = messages[index - 1];
      if (
        (message.collection && !prevMessage.collection) ||
        (message.collection &&
          prevMessage.collection &&
          message.collection.collectionId !== prevMessage.collection.collectionId) ||
        (!message.collection && prevMessage.collection)
      ) {
        // Insert scoped message
        processedMessages.push({ ...SCOPED_MESSAGE, collection: message.collection });
      }
      // Add the current message
      processedMessages.push(message);
    }
  });
  return processedMessages;
};

// Utility functions for message chunk grouping and linking

//  collect all ids from a chunk that could link it to a tool call
export const collectLinkIds = (chunk) => {
  const ids = [];
  if (chunk.data?.tool_call_id) {
    ids.push(chunk.data.tool_call_id);
  }
  if (chunk.data?.id) {
    ids.push(chunk.data.id);
  }
  if (chunk.type === 'messages' && Array.isArray(chunk.data)) {
    for (const msg of chunk.data) {
      if (msg.content?.id) {
        ids.push(msg.content.id);
      }
      if (msg.delta?.fields?.id) {
        ids.push(msg.delta.fields.id);
      }
    }
  }
  return ids;
};

// A tool call either finishes normally or raises (tool-error instead of tool-finished, never
// both) — both are terminal states for the group, so `done` should flip on either.
const isTerminalToolEvent = (event) => event === TOOL_CALL_TYPE.TOOL_FINISHED || event === TOOL_CALL_TYPE.TOOL_ERROR;

// create or update a tool call group
export const addToToolCallGroup = (chunk, result, toolCallMap) => {
  const id = chunk.data.tool_call_id;
  if (toolCallMap.has(id)) {
    const existingIndex = toolCallMap.get(id);
    result[existingIndex].events.push(chunk);
    if (isTerminalToolEvent(chunk.data.event)) {
      result[existingIndex].done = true;
    }
  } else {
    toolCallMap.set(id, result.length);
    result.push({
      type: CHUNK_TYPE.TOOL_CALLS,
      data: chunk.data,
      done: isTerminalToolEvent(chunk.data.event),
      events: [chunk],
    });
  }
};

// attach chunk to all matching tool call groups
export const attachToToolCallGroups = (chunk, idsToCheck, result, toolCallMap) => {
  for (const id of idsToCheck) {
    if (toolCallMap.has(id)) {
      const groupIndex = toolCallMap.get(id);
      if (!result[groupIndex].events.includes(chunk)) {
        result[groupIndex].events.push(chunk);
      }
    }
  }
};

// Sanitize and parse a single piece of markdown text, turning citation refs into placeholder
// spans (later hydrated with InfoTooltip) and ensuring links open in a new tab.
export const sanitizeAndParse = (content, isTyping = false) => {
  const html = content?.replace(
    /\[([^\]]+?)-(\d+),\s*p\.(\d+)\]/g,
    (_match, id, snippetIndex, page) =>
      `<span class="citation-placeholder" data-id="${id}" data-index="${snippetIndex}" data-page="${page}"></span>`,
  );

  const toParse = html !== undefined ? html : '';
  const parsed = isTyping ? marked.parse(toParse + '<span v-if="isTyping" class="cursor" />') : marked.parse(toParse);

  const sanitized = DOMPurify.sanitize(parsed);
  return ensureLinksOpenInNewTab(sanitized);
};
